import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { User, Mail, Phone, FileText, Save, Camera } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfileSettings() {
  const navigate = useNavigate();
  const { user, setUser, updateUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    bio: '',
    picture: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        picture: user.picture || ''
      });
    }
  }, [user]);

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({ ...prev, picture: response.data.url }));
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        name: formData.name,
        username: formData.username,
        phone: formData.phone,
        bio: formData.bio
      };
      
      // Only include email if it changed
      if (formData.email && formData.email !== user?.email) {
        updateData.email = formData.email;
      }
      
      const response = await apiClient.put('/user/profile', updateData);

      // Update user in store (merge to preserve role and other fields)
      updateUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10 max-w-3xl">
        <Card className="glass-default border-white/10">
          <CardHeader>
            <CardTitle className="text-3xl font-bold gradient-text">Profile Settings</CardTitle>
            <p className="text-white/60">Update your personal information</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Profile Picture */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  {formData.picture ? (
                    <img
                      src={formData.picture}
                      alt="Profile"
                      className="w-24 h-24 rounded-full border-4 border-violet-500 object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full border-4 border-violet-500 bg-violet-500/20 flex items-center justify-center">
                      <User size={40} className="text-violet-400" />
                    </div>
                  )}
                  <input
                    type="file"
                    id="picture-upload"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('picture-upload').click()}
                    disabled={uploading}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-violet-600 hover:bg-violet-500 rounded-full flex items-center justify-center border-2 border-background transition-colors"
                  >
                    <Camera size={16} className="text-white" />
                  </button>
                </div>
                <div>
                  <p className="font-semibold mb-1">{user?.name}</p>
                  <p className="text-sm text-white/60">{user?.email}</p>
                  <p className="text-xs text-white/40 mt-2">
                    Role: <span className="capitalize">{user?.role}</span>
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2 text-white">
                  <Mail size={18} />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                  placeholder="your@email.com"
                />
                <p className="text-xs text-white/40">
                  Note: Changing your email will update your account login
                </p>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2 text-white">
                  <User size={18} />
                  Full Name *
                </Label>
                <Input
                  data-testid="name-input"
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                  placeholder="Your full name"
                />
              </div>

              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="username" className="flex items-center gap-2 text-white">
                  <User size={18} />
                  Username / Handle
                </Label>
                <Input
                  data-testid="username-input"
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                  placeholder="@yourname"
                />
                <p className="text-xs text-white/40">Used for @mentions in chat</p>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2 text-white">
                  <Phone size={18} />
                  Phone Number
                </Label>
                <Input
                  data-testid="phone-input"
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio" className="flex items-center gap-2 text-white">
                  <FileText size={18} />
                  Bio / About
                </Label>
                <textarea
                  data-testid="bio-input"
                  id="bio"
                  rows="4"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder:text-white/30 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 outline-none"
                  placeholder="Tell us a bit about yourself..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  data-testid="save-button"
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-12 text-lg font-bold rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500"
                >
                  <Save size={20} className="mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  onClick={() => navigate(-1)}
                  variant="outline"
                  className="border-white/20 hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </form>

            {/* Artist-specific settings */}
            {user?.role === 'artist' && (
              <div className="pt-6 border-t border-white/10">
                <h3 className="text-xl font-bold mb-4">Artist Settings</h3>
                <Button
                  onClick={() => navigate('/artist/edit-profile')}
                  variant="outline"
                  className="w-full border-white/20 hover:bg-white/10"
                >
                  Edit Artist Profile & Music Links
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
